// HowItWorksWithProgress.jsx
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const faqs = [
  {
    question: "How do I add multiple rounds?",
    answer: 'After creating an interview, click "Add Round" and repeat for as many rounds as needed.',
  },
  {
    question: "Can I attach documents to questions?",
    answer: "Yes, you can attach PDFs or Word docs to any question for future reference.",
  },
  {
    question: "Is my data private?",
    answer: "Absolutely. Your data is securely stored and only accessible by you.",
  },
  {
    question: "Can I export my interview data?",
    answer: "Yes, the dashboard table includes a CSV export feature for your interviews.",
  },
];

export default function HowItWorksWithProgress() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const stepsRefs = useRef([]);
  stepsRefs.current = [];

  const navigate = useNavigate();

  const steps = [
    {
      step: "Step 1: Add an Interview",
      desc: "Fill metadata like company, position, date, status (Scheduled, Pending, Completed). Optional: Start and End time. Click Create Interview to proceed to rounds page.",
      img: "screenshots/add-interview.png",
      alt: "Add Interview page screenshot",
    },
    {
      step: "Step 2: Add Rounds",
      desc: "Add new rounds with a name and optional notes. Edit or delete rounds anytime. After adding rounds, start managing questions.",
      img: "screenshots/add-round.png",
      alt: "Add Round screenshot",
    },
    {
      step: "Step 3: Manage Questions",
      desc: "Add, edit, or delete questions for each round. Mark as important, set difficulty (Easy/Hard), add answers, and attach PDFs or Word docs. Click Finish Round when done.",
      img: "screenshots/manage-questions.png",
      alt: "Manage Questions screenshot",
    },
    {
      step: "Step 4: Dashboard & Stats",
      desc: "View stats like total interviews, rounds, and questions. Filter by company, status, or date. Notifications alert 24 hours before scheduled interviews. Table includes pagination and CSV export.",
      img: "screenshots/dashboard.png",
      alt: "Dashboard screenshot",
    },
    {
      step: "Step 5: Interview Timeline",
      desc: "Track all events with timestamps: interview created, status updates, and notes. Duration shown if start/end times are added.",
      img: "screenshots/timeline.png",
      alt: "Interview Timeline screenshot",
    },
    {
      step: "Step 6: Study Material & Mock Tests",
      desc: "Upload study materials (PDF, DOCX, etc.) and add notes. Create mock tests with 20 questions, then practice with timer and camera. Audio questions are read out, answers recorded for future reference.",
      img: "screenshots/mock-test.png",
      alt: "Mock Test screenshot",
    },
  ];

  const toggleFaq = (index) => setOpenFaqIndex(openFaqIndex === index ? null : index);

  const addToRefs = (el) => {
    if (el && !stepsRefs.current.includes(el)) stepsRefs.current.push(el);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = stepsRefs.current.indexOf(entry.target);
            setCurrentStep(index);
          }
        });
      },
      { threshold: 0.5 }
    );

    stepsRefs.current.forEach((el) => observer.observe(el));

    return () => stepsRefs.current.forEach((el) => observer.unobserve(el));
  }, []);

  return (
    <section className="max-w-6xl mx-auto p-6 dark:bg-gray-900 dark:text-gray-100 transition-colors duration-300 relative">
      
      {/* ====== Styled Back Button ====== */}
      <div className="mb-6 text-left">
        <button
          onClick={() => navigate("/")} // Back to landing page
          className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-semibold px-6 py-3 rounded-lg shadow hover:scale-105 transition-transform duration-300"
        >
          ← Back to Landing Page
        </button>
      </div>

      <h1 className="text-4xl font-bold mb-8 text-center">How InterviewNotebook Works</h1>

      {/* Vertical Progress Bar */}
      <div className="hidden md:block fixed top-1/4 left-4 h-1/2 w-2 bg-gray-300 dark:bg-gray-700 rounded">
        <div
          className="w-2 bg-blue-500 rounded transition-all duration-300"
          style={{
            height: `${((currentStep + 1) / steps.length) * 100}%`,
          }}
        ></div>
      </div>

      {/* Step-by-Step Workflow */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6">Step-by-Step Workflow</h2>

        {steps.map((item, idx) => (
          <motion.div
            key={idx}
            ref={addToRefs}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: idx * 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center mb-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                  currentStep === idx ? "bg-blue-500 text-white" : "bg-gray-300 dark:bg-gray-700 text-gray-500"
                }`}
              >
                {idx + 1}
              </div>
              <h3 className="text-xl font-semibold">{item.step}</h3>
            </div>
            <p className="mb-2">{item.desc}</p>
            <div className="mt-2 rounded shadow-md cursor-pointer w-full max-w-3xl h-48 bg-gray-300 dark:bg-gray-700 flex items-center justify-center">
              <span className="text-gray-600 dark:text-gray-300">Screenshot Placeholder</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* FAQ Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div key={idx} className="border rounded p-4 dark:border-gray-700">
              <button
                onClick={() => toggleFaq(idx)}
                className="w-full text-left font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <span>{openFaqIndex === idx ? "−" : "+"}</span>
              </button>
              {openFaqIndex === idx && <p className="mt-2">{faq.answer}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
